import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, Link as LinkIcon, ExternalLink, X, Tag } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";

const EMPTY = {
  name: "", season: "", logo_url: "", start_date: "", end_date: "",
  slug: "", hero_title: "", hero_subtitle: "", hero_bg_url: "",
  categories: [],
};

export default function AdminTournaments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/tournaments?include_archived=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/tournaments/${editing.id}`, form);
        toast.success("Tournament updated");
      } else {
        await api.post("/tournaments", form);
        toast.success("Tournament created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tournament "${t.name}"? This cannot be undone.`)) return;
    await api.delete(`/tournaments/${t.id}`);
    toast.success("Deleted");
    load();
  };

  const archive = async (t) => {
    await api.post(`/tournaments/${t.id}/archive`);
    toast.success(`Torneio arquivado: ${t.name}`);
    load();
  };

  const unarchive = async (t) => {
    await api.post(`/tournaments/${t.id}/unarchive`);
    toast.success(`Torneio restaurado: ${t.name}`);
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Tournaments"
        subtitle="Create and manage seasons & competitions"
        testid="page-tournaments"
        action={
          <Button data-testid="create-tournament-btn" onClick={startCreate} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
            <Plus size={16} className="mr-2" /> New tournament
          </Button>
        }
      />

      <div className="border border-navy-border rounded-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 bg-navy-surface text-[10px] font-heading uppercase tracking-widest text-ink-muted">
          <div className="col-span-1">Logo</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Season</div>
          <div className="col-span-3">Slug / Public URL</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {items.length === 0 ? (
          <div className="p-12 text-center text-ink-muted">No tournaments yet.</div>
        ) : items.map((t) => (
          <div key={t.id} className={`grid grid-cols-12 gap-2 items-center px-4 py-3 border-t border-navy-border ${t.archived ? "opacity-60" : ""}`} data-testid={`tournament-row-${t.id}`}>
            <div className="col-span-2 md:col-span-1">
              <div className="w-10 h-10 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden">
                {t.logo_url ? <img src={fileUrl(t.logo_url)} alt="" className="w-full h-full object-cover" />
                  : <span className="font-heading font-black text-orange">{t.name?.[0]}</span>}
              </div>
            </div>
            <div className="col-span-10 md:col-span-3 font-heading uppercase tracking-tight text-white">{t.name}</div>
            <div className="col-span-6 md:col-span-2 text-ink-secondary text-sm">{t.season || "—"}</div>
            <div className="col-span-6 md:col-span-3 text-ink-muted text-xs font-mono truncate">
              {t.slug ? (
                <Link to={`/t/${t.slug}`} target="_blank" className="hover:text-orange inline-flex items-center gap-1" data-testid={`tournament-slug-link-${t.id}`}>
                  /t/{t.slug} <ExternalLink size={10} />
                </Link>
              ) : "—"}
            </div>
            <div className="col-span-6 md:col-span-1">
              {t.archived ? (
                <span className="inline-block px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest rounded-sm bg-navy-surface text-ink-muted border border-navy-border" data-testid={`tournament-archived-badge-${t.id}`}>
                  Arquivado
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest rounded-sm bg-court-green/20 text-court-green">
                  Ativo
                </span>
              )}
            </div>
            <div className="col-span-6 md:col-span-2 flex justify-end gap-1 flex-wrap">
              {t.archived ? (
                <Button data-testid={`unarchive-tournament-${t.id}`} size="icon" variant="ghost" onClick={() => unarchive(t)} title="Restaurar" className="text-ink-secondary hover:text-court-green hover:bg-navy-surface"><ArchiveRestore size={14} /></Button>
              ) : (
                <Button data-testid={`archive-tournament-${t.id}`} size="icon" variant="ghost" onClick={() => archive(t)} title="Arquivar" className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Archive size={14} /></Button>
              )}
              <Button data-testid={`edit-tournament-${t.id}`} size="icon" variant="ghost" onClick={() => startEdit(t)} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
              <Button data-testid={`delete-tournament-${t.id}`} size="icon" variant="ghost" onClick={() => remove(t)} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit tournament" : "New tournament"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="tournament-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Name</Label>
                <Input data-testid="tournament-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Season</Label>
                <Input value={form.season || ""} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2026" className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs flex items-center gap-1">Slug (URL) <LinkIcon size={10}/></Label>
                <Input data-testid="tournament-slug-input" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto do nome" className="bg-navy border-navy-border mt-2 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Start date</Label>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">End date</Label>
                <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <ImageUpload label="Logo" value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} testid="tournament-logo" />
              </div>
            </div>

            <div className="border-t border-navy-border pt-4">
              <div className="font-heading uppercase tracking-widest text-orange text-xs mb-3 flex items-center gap-1"><Tag size={12}/> Categorias</div>
              <p className="text-ink-muted text-xs mb-3">Ex.: Sub-15, Adulto Masculino, Adulto Feminino. Cada time pertence a uma categoria.</p>
              <CategoriesEditor value={form.categories || []} onChange={(cats) => setForm({ ...form, categories: cats })} />
            </div>

            <div className="border-t border-navy-border pt-4">
              <div className="font-heading uppercase tracking-widest text-orange text-xs mb-3">Página pública do torneio (opcional)</div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-ink-secondary uppercase tracking-widest text-xs">Hero title</Label>
                  <Input data-testid="tournament-hero-title-input" value={form.hero_title || ""} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} placeholder="ex.: Copa Verão 3x3 · Chega chegando" className="bg-navy border-navy-border mt-2 rounded-sm" />
                </div>
                <div>
                  <Label className="text-ink-secondary uppercase tracking-widest text-xs">Hero subtitle</Label>
                  <Input data-testid="tournament-hero-subtitle-input" value={form.hero_subtitle || ""} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} placeholder="linha curta que aparece embaixo do título" className="bg-navy border-navy-border mt-2 rounded-sm" />
                </div>
                <ImageUpload label="Hero background image" value={form.hero_bg_url} onChange={(url) => setForm({ ...form, hero_bg_url: url })} testid="tournament-hero-bg" />
              </div>
            </div>

            <DialogFooter>
              <Button data-testid="tournament-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
                {editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoriesEditor({ value, onChange }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if ((value || []).some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.warning("Categoria já existe");
      return;
    }
    onChange([...(value || []), { name }]);
    setDraft("");
  };
  const remove = (idx) => onChange((value || []).filter((_, i) => i !== idx));
  return (
    <div>
      <div className="flex gap-2">
        <Input
          data-testid="tournament-category-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Nome da categoria (ex.: Sub-15)"
          className="bg-navy border-navy-border rounded-sm flex-1"
        />
        <Button
          type="button"
          onClick={add}
          data-testid="tournament-category-add-btn"
          className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm h-10 px-3"
        >
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="tournament-category-list">
          {value.map((c, i) => (
            <span key={c.id || c.name} data-testid={`tournament-category-chip-${i}`} className="inline-flex items-center gap-1 bg-navy border border-navy-border rounded-sm px-2 py-1 text-sm">
              <Tag size={10} className="text-orange" />
              {c.name}
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`tournament-category-remove-${i}`}
                className="ml-1 text-ink-muted hover:text-court-red"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

